import { useMemo } from 'react'
import { Icons } from './design_system/components/Icons/Icons.jsx'

const getPageRange = (page, totalPages, size) => {
    page = parseInt(page);
    let start = 1;
    let end = totalPages;
    end = page + size > end ? end : page + size;
    start = page - size < start ? start : page - size;
    if (end - start < (2 * size)) {
        end = (start === 1) ? (start + (2 * size) > totalPages ? totalPages : start + (2 * size)) : end;
        start = end === totalPages ? (end - (2 * size) < 1 ? 1 : end - (2 * size)) : start;
    }

    let ret = [];
    if (!end) {
        end = start;
        start = 0;
    }
    for (let i = start; i <= end; i++) {
        ret.push(i);
    }
    return ret;
};

export const getBoundedPageItems = (currentPage, totalPages) => {
    const total = Math.max(parseInt(totalPages, 10) || 0, 0);
    if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

    const page = Math.min(Math.max(parseInt(currentPage, 10) || 1, 1), total);
    const start = Math.max(1, Math.min(page - 2, total - 4));
    const end = Math.min(total, start + 4);
    const window = Array.from({ length: end - start + 1 }, (_, index) => start + index);

    const items = start <= 3
        ? Array.from({ length: end }, (_, index) => index + 1)
        : [1, 'leading-ellipsis', ...window];

    return end < total ? [...items, 'trailing-ellipsis'] : items;
};

const BoundedPagination = ({ totalPages, currentPage, onPageChange, style }) => {
    const total = Math.max(parseInt(totalPages, 10) || 0, 0);
    const page = Math.min(Math.max(parseInt(currentPage, 10) || 1, 1), total || 1);
    const items = useMemo(() => getBoundedPageItems(page, total), [page, total]);

    if (total <= 1) return null;

    return (
        <nav aria-label="Pagination" style={{ justifyContent: 'center', color: 'var(--color-text-secondary)', textAlign: 'center', padding: 'var(--space-s)', ...style }} className="flex items-center">
            <button
                type="button"
                aria-label="Previous page"
                disabled={page === 1}
                onClick={() => onPageChange(page - 1)}
                className="pagination-icon-container"
            >
                <span className="pagination-icon flex items-center justify-center">
                    <Icons name="chevron-left" size={16} />
                </span>
            </button>

            <span>
                {items.map(item => typeof item === 'string' ? (
                    <span key={item} className="pagination-ellipsis" aria-hidden="true">…</span>
                ) : (
                    <button
                        type="button"
                        onClick={() => onPageChange(item)}
                        key={item}
                        aria-label={`Page ${item}`}
                        aria-current={page === item ? 'page' : undefined}
                        className={`pagination-number body-s-semibold ${page === item ? 'active' : ''}`}
                    >
                        {item}
                    </button>
                ))}
            </span>

            <button
                type="button"
                aria-label="Next page"
                disabled={page === total}
                onClick={() => onPageChange(page + 1)}
                className="pagination-icon-container"
            >
                <span className="pagination-icon flex items-center justify-center">
                    <Icons name="chevron-right" size={16} />
                </span>
            </button>
        </nav>
    );
};

const LegacyPagination = ({ totalPages, currentPage, onPageChange, style }) => {
    const { pages, showFirstLink, showLastLink } = useMemo(() => {
        const pages = getPageRange(currentPage, totalPages, 3);
        return {
            pages,
            showFirstLink: pages[0] !== 1,
            showLastLink: pages[pages.length - 1] !== totalPages,
        };
    }, [currentPage, totalPages]);

    if (!totalPages || totalPages <= 1) return null;

    return (
        <div style={{ justifyContent: 'center', color: 'var(--color-text-secondary)', textAlign: 'center', padding: 'var(--space-s)', ...style }} className="flex items-center">
            {currentPage !== 1 && (
                <div onClick={() => onPageChange(currentPage - 1)} className="pagination-icon-container">
                    <div className="pagination-icon flex items-center justify-center">
                        <Icons name="chevron-left" size={16} />
                    </div>
                </div>
            )}

            <span>
                {showFirstLink && (
                    <span className="pagination-number body-s-semibold" onClick={() => onPageChange(1)}>
                        1
                    </span>
                )}

                {pages[0] !== 2 && showFirstLink && <span>...</span>}

                {pages.map(page => (
                    <span
                        onClick={() => onPageChange(page)}
                        key={page}
                        className={`pagination-number body-s-semibold ${currentPage === page ? 'active' : ''}`}
                    >
                        {page}
                    </span>
                ))}

                {pages[pages.length - 1] !== totalPages - 1 && showLastLink && <span>...</span>}

                {showLastLink && (
                    <span className="pagination-number body-s-semibold" onClick={() => onPageChange(totalPages)}>
                        {totalPages}
                    </span>
                )}
            </span>

            {currentPage !== totalPages && (
                <div onClick={() => onPageChange(currentPage + 1)} className="pagination-icon-container">
                    <div className="pagination-icon flex items-center justify-center">
                        <Icons name="chevron-right" size={16} />
                    </div>
                </div>
            )}
        </div>
    );
};

const Pagination = ({ variant = 'legacy', ...props }) => (
    variant === 'bounded' ? <BoundedPagination {...props} /> : <LegacyPagination {...props} />
);

export default Pagination;
